import React, { useState } from "react";
import { X, HelpCircle } from "lucide-react";

const STORAGE_KEY = "ravensharp_pod_guide_dismissed";

/**
 * Closeable step-by-step "how this works" guide.
 * Dismissal is remembered in localStorage so it doesn't reappear every visit.
 * A small "?" button stays available to reopen it at any time.
 */
export default function HowToGuide() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
  };

  const reopen = () => {
    setDismissed(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  if (dismissed) {
    return (
      <button onClick={reopen}
        className="mb-6 flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--raven-glow)] transition-colors">
        <HelpCircle className="w-3.5 h-3.5" /> How does this work?
      </button>
    );
  }

  const steps = [
    { n: 1, title: "Upload artwork", body: "Add source images or send generated artwork into the pipeline. No provider connection is needed yet." },
    { n: 2, title: "Set the run", body: "Choose market and pricing guidance. The artwork is processed once, independently of any provider." },
    { n: 3, title: "Process", body: "Raven Sharp upscales, prepares print files, analyses product fit and writes reusable SEO copy." },
    { n: 4, title: "Review & approve", body: "Edit titles, tags, prices and products. Nothing is sent anywhere while you review." },
    { n: 5, title: "Choose destination", body: "Choose a provider to create separate unpublished product drafts. Each must receive at least one authentic matching mockup before it is marked ready." },
  ];

  return (
    <div className="mb-8 rounded-2xl border border-[var(--raven)]/25 bg-[var(--raven)]/[0.06] p-5 relative">
      <button onClick={dismiss} aria-label="Close guide"
        className="absolute top-4 right-4 text-[var(--muted)] hover:text-white transition-colors">
        <X className="w-4 h-4" />
      </button>
      <h2 className="text-sm font-semibold mb-4 pr-8">How the pipeline works</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {steps.map(s => (
          <div key={s.n} className="flex gap-2.5">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--raven)]/25 border border-[var(--raven)]/40 flex items-center justify-center text-xs font-bold text-[var(--raven-glow)]">
              {s.n}
            </div>
            <div>
              <div className="text-xs font-semibold mb-0.5">{s.title}</div>
              <div className="text-xs text-[var(--muted)] leading-snug">{s.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
